import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePriceHistory } from "@/hooks/usePriceHistory";
import { Loader2 } from "lucide-react";

interface ProbabilityChartProps {
  marketId: string;
  selectedOutcome?: "YES" | "NO";
  onOutcomeHover?: (outcome: "YES" | "NO" | null) => void;
  yesPrice?: number;
  noPrice?: number;
}

const ProbabilityChart = ({ marketId, selectedOutcome = "YES", onOutcomeHover, yesPrice = 50, noPrice = 50 }: ProbabilityChartProps) => {
  const {
    priceHistory,
    isLoading,
    liveYesPrice,
    liveNoPrice,
    hasLiveData,
    oddsError
  } = usePriceHistory(marketId);

  // Use live prices if available, fallback to props
  const currentYesPrice = liveYesPrice || yesPrice;
  const currentNoPrice = liveNoPrice || noPrice;
  return (
    <Card className="p-6 bg-card border-border">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-foreground">Price History</h3>
          <Tabs defaultValue="30d" className="w-auto">
            <TabsList>
              <TabsTrigger value="24h">24h</TabsTrigger>
              <TabsTrigger value="7d">7d</TabsTrigger>
              <TabsTrigger value="30d">30d</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full transition-all ${selectedOutcome === "YES"
              ? "bg-success ring-2 ring-success ring-offset-2"
              : "bg-success/50"
              }`} />
            <span className={`font-medium transition-colors ${selectedOutcome === "YES" ? "text-success" : "text-muted-foreground"
              }`}>YES</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full transition-all ${selectedOutcome === "NO"
              ? "bg-destructive ring-2 ring-destructive ring-offset-2"
              : "bg-destructive/50"
              }`} />
            <span className={`font-medium transition-colors ${selectedOutcome === "NO" ? "text-destructive" : "text-muted-foreground"
              }`}>NO</span>
          </div>
        </div>

        {/* Chart */}
        <div className="h-[300px] w-full">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={priceHistory}
                onMouseMove={(e) => {
                  // Détection du hover sur les lignes
                  if (e && e.activePayload) {
                    const payload = e.activePayload;
                    if (payload[0]?.name === "yes" || payload[1]?.name === "yes") {
                      onOutcomeHover?.("YES");
                    } else if (payload[0]?.name === "no" || payload[1]?.name === "no") {
                      onOutcomeHover?.("NO");
                    }
                  }
                }}
                onMouseLeave={() => onOutcomeHover?.(null)}
              >
                <defs>
                  <linearGradient id="colorYes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorNo" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis
                  dataKey="date"
                  stroke="hsl(var(--muted-foreground))"
                  style={{ fontSize: '12px' }}
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis
                  domain={[0, 100]}
                  stroke="hsl(var(--muted-foreground))"
                  style={{ fontSize: '12px' }}
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    color: 'hsl(var(--foreground))',
                    padding: '12px'
                  }}
                  labelStyle={{ color: 'hsl(var(--muted-foreground))', marginBottom: '4px' }}
                  formatter={(value: number, name: string) => {
                    const label = name === 'yes' ? 'YES' : 'NO';
                    const color = name === 'yes' ? 'hsl(var(--success))' : 'hsl(var(--destructive))';
                    return [
                      <span style={{ color, fontWeight: 600 }}>{value}%</span>,
                      <span style={{ color }}>{label}</span>
                    ];
                  }}
                />

                {/* YES Line avec surbrillance */}
                <Line
                  type="monotone"
                  dataKey="yes"
                  stroke="hsl(var(--success))"
                  strokeWidth={selectedOutcome === "YES" ? 3 : 2}
                  dot={false}
                  activeDot={{
                    r: selectedOutcome === "YES" ? 6 : 4,
                    fill: "hsl(var(--success))",
                    stroke: "hsl(var(--background))",
                    strokeWidth: 2
                  }}
                  opacity={selectedOutcome === "YES" ? 1 : 0.4}
                  className="transition-all duration-300"
                />

                {/* NO Line avec surbrillance */}
                <Line
                  type="monotone"
                  dataKey="no"
                  stroke="hsl(var(--destructive))"
                  strokeWidth={selectedOutcome === "NO" ? 3 : 2}
                  dot={false}
                  activeDot={{
                    r: selectedOutcome === "NO" ? 6 : 4,
                    fill: "hsl(var(--destructive))",
                    stroke: "hsl(var(--background))",
                    strokeWidth: 2
                  }}
                  opacity={selectedOutcome === "NO" ? 1 : 0.4}
                  className="transition-all duration-300"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Price indicators */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className={`text-sm transition-all ${selectedOutcome === "YES" ? "scale-105" : ""}`}>
            <p className="text-muted-foreground mb-1">
              YES Price
              {hasLiveData && (
                <span className="text-xs text-green-600 ml-1">● Live</span>
              )}
            </p>
            <p className={`text-2xl font-bold ${selectedOutcome === "YES" ? "text-success" : "text-foreground"}`}>
              {currentYesPrice.toFixed(1)}%
            </p>
            {priceHistory.length > 1 && (
              <p className="text-xs text-muted-foreground">
                Initial: {priceHistory[0]?.yes?.toFixed(1)}% → Current: {currentYesPrice.toFixed(1)}%
              </p>
            )}
          </div>
          <div className={`text-sm text-right transition-all ${selectedOutcome === "NO" ? "scale-105" : ""}`}>
            <p className="text-muted-foreground mb-1">
              NO Price
              {hasLiveData && (
                <span className="text-xs text-green-600 ml-1">● Live</span>
              )}
            </p>
            <p className={`text-2xl font-bold ${selectedOutcome === "NO" ? "text-destructive" : "text-foreground"}`}>
              {currentNoPrice.toFixed(1)}%
            </p>
            {priceHistory.length > 1 && (
              <p className="text-xs text-muted-foreground">
                Initial: {priceHistory[0]?.no?.toFixed(1)}% → Current: {currentNoPrice.toFixed(1)}%
              </p>
            )}
          </div>
        </div>

        {/* Price progression summary */}
        {priceHistory.length > 1 && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Price Progression:</span>
              <span className="ml-2">
                {priceHistory[0]?.yes?.toFixed(1)}% → {currentYesPrice.toFixed(1)}%
                ({currentYesPrice > priceHistory[0]?.yes ? '+' : ''}{(currentYesPrice - priceHistory[0]?.yes).toFixed(1)}%)
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Data Source:</span>
              <span className="ml-2">
                {hasLiveData ? 'Live (PancakeSwap V4)' : 'Historical (Subgraph)'}
              </span>
            </div>
          </div>
        )}

        {/* Live data status */}
        {hasLiveData && (
          <div className="flex items-center justify-center pt-2">
            <div className="flex items-center gap-2 text-xs text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Live prices from PancakeSwap V4</span>
            </div>
          </div>
        )}

        {oddsError && (
          <div className="flex items-center justify-center pt-2">
            <div className="flex items-center gap-2 text-xs text-orange-500">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span>Using fallback prices</span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ProbabilityChart;
